import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card, { CardTitle, CardText } from 'react-md/lib/Cards';
import TextField from 'react-md/lib/TextFields';
import DateInput from 'Components/Common/DateInput/DateInput';
import LoadingButton from 'Components/Common/LoadingButton';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
   IdentityActions,
   IdentityTypes,
   selectMfaConfig,
   selectMfaStatistics,
} from 'State/Redux/IdentityRedux';
import { selectIsLoading, selectIsSuccess } from 'State/Redux/AsyncRedux';
import { isEnterKeyPress } from 'Util/Helpers/Input';
import './MfaPolicy.scss';

const MfaPolicy = (): JSX.Element => {
   const dispatch = useDispatch();

   const mfaConfig = useSelector(selectMfaConfig);
   const mfaStatistics = useSelector(selectMfaStatistics);

   const isConfigLoading = useSelector(
      selectIsLoading([IdentityTypes.GET_MFA_CONFIG_REQUEST])
   );
   const isStatsLoading = useSelector(
      selectIsLoading([IdentityTypes.GET_MFA_STATISTICS_REQUEST])
   );
   const isSaving = useSelector(
      selectIsLoading([
         IdentityTypes.UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_REQUEST,
      ])
   );
   const isSaveSuccess = useSelector(
      selectIsSuccess([
         IdentityTypes.UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_REQUEST,
      ])
   );

   const [enforcementDate, setEnforcementDate] = useState<string | null>(null);
   const [signinBannerMessage, setSigninBannerMessage] = useState<string>('');
   const [submitted, setSubmitted] = useState(false);
   const wasSaving = useRef(false);

   useEffect((): void => {
      dispatch(IdentityActions.getMfaConfigRequest());
      dispatch(IdentityActions.getMfaStatisticsRequest());
   }, [dispatch]);

   useEffect((): void => {
      if (mfaConfig) {
         setEnforcementDate(mfaConfig.enforcementDate ?? null);
         setSigninBannerMessage(mfaConfig.enforcementDateMessage ?? '');
      }
   }, [mfaConfig]);

   const normalizeDate = (d: string | null | undefined): string | null => {
      if (!d || !moment(d).isValid()) return null;
      return moment(d).format('YYYY-MM-DD');
   };

   const hasChanged =
      normalizeDate(enforcementDate) !==
         normalizeDate(mfaConfig?.enforcementDate) ||
      signinBannerMessage !== (mfaConfig?.enforcementDateMessage ?? '');

   const isValid = !!enforcementDate && !!signinBannerMessage.trim();

   useEffect((): void => {
      if (wasSaving.current && !isSaving && isSaveSuccess) {
         toast.success('MFA policy updated successfully.');
      }
      wasSaving.current = isSaving;
   }, [isSaving, isSaveSuccess]);

   const handleSave = (): void => {
      setSubmitted(true);
      if (!isValid) return;
      dispatch(
         IdentityActions.updateMfaEnforcementDateAndMessageRequest(
            enforcementDate,
            signinBannerMessage || null
         )
      );
   };

   const daysUntil =
      enforcementDate && moment(enforcementDate).isValid()
         ? moment(enforcementDate)
              .startOf('day')
              .diff(moment().startOf('day'), 'days')
         : null;

   const chipLabel =
      daysUntil === null
         ? null
         : daysUntil > 0
         ? `${daysUntil} day${daysUntil !== 1 ? 's' : ''} until enforcement`
         : daysUntil === 0
         ? 'Enforcement is today'
         : 'Enforcement date has passed';

   const authenticatorEnrolled =
      mfaStatistics?.totalAuthenticatorEnrolledUsers ?? null;

   const smsEnrolled = mfaStatistics?.totalSmsEnrolledUsers ?? null;

   const unenrolled =
      mfaStatistics !== null
         ? Math.max(
              0,
              mfaStatistics.totalActiveUsers -
                 mfaStatistics.totalAuthenticatorEnrolledUsers -
                 mfaStatistics.totalSmsEnrolledUsers
           )
         : null;

   const formatStat = (value: number | null): string =>
      value !== null ? value.toLocaleString() : '—';

   const isLoading = isConfigLoading || isStatsLoading;

   return (
      <div className="md-grid md-cell--12">
         <div className="md-cell md-cell--12 mfa-policy-card-wrapper">
            <Card>
               <CardTitle title="MFA Policy" />
               <CardText>
                  <p className="mfa-policy-description">
                     The single fixed cutoff when the skip window closes.
                     Unenrolled users must complete MFA setup before access at
                     next login after this date. Set once; no deployment
                     required.
                  </p>

                  <div className="mfa-policy-section-divider" />

                  <div className="mfa-policy-form-section md-grid">
                     <div className="md-cell md-cell--6">
                        <h4 className="mfa-policy-form-label">
                           Enforcement Date
                        </h4>
                        <p className="mfa-policy-description">
                           All unenrolled users will be required to set up MFA
                           at their next login on or after this date. Enrolled
                           users and active sessions are unaffected.
                        </p>
                     </div>
                     <div className="md-cell md-cell--6 mfa-policy-date-col">
                        <div className="mfa-policy-date-row">
                           <DateInput
                              id="mfa-enforcement-date"
                              className="mfa-policy-date-input"
                              value={enforcementDate}
                              disabled={isConfigLoading}
                              onChange={(date): void =>
                                 setEnforcementDate(date || null)
                              }
                           />
                        </div>
                        {submitted && !enforcementDate && (
                           <span className="mfa-policy-field-error">
                              Enforcement date is required.
                           </span>
                        )}
                        {chipLabel && (
                           <span
                              className={`mfa-policy-chip${
                                 daysUntil !== null && daysUntil < 0
                                    ? ' mfa-policy-chip--warn'
                                    : ''
                              }`}
                           >
                              {chipLabel}
                           </span>
                        )}
                     </div>
                  </div>

                  <div className="mfa-policy-section-divider" />

                  <div className="mfa-policy-form-section md-grid">
                     <div className="md-cell md-cell--6">
                        <h4 className="mfa-policy-form-label">Signin Banner</h4>
                        <p className="mfa-policy-description">
                           Shown to unenrolled users at login during the skip
                           window. Content derived from the enforcement date
                           above.
                        </p>
                     </div>
                     <div className="md-cell md-cell--6 mfa-policy-date-col">
                        <TextField
                           id="mfa-signin-banner-message"
                           type="text"
                           placeholder="Enter banner message"
                           value={signinBannerMessage}
                           maxLength={200}
                           disabled={isConfigLoading}
                           onChange={(value): void =>
                              setSigninBannerMessage(value as string)
                           }
                           fullWidth
                           error={submitted && !signinBannerMessage.trim()}
                           errorText="Signin banner message is required."
                        />
                     </div>
                  </div>

                  <div className="mfa-policy-save-row">
                     <LoadingButton
                        flat
                        primary
                        swapTheming
                        isLoading={isSaving}
                        disabled={isConfigLoading || !hasChanged}
                        onClick={handleSave}
                        onKeyUp={(keyPress): void => {
                           if (!isEnterKeyPress(keyPress)) return;
                           handleSave();
                        }}
                     >
                        Save
                     </LoadingButton>
                  </div>
               </CardText>
            </Card>
         </div>

         <div className="md-cell md-cell--12 mfa-policy-card-wrapper">
            <Card>
               <CardTitle
                  title="MFA Adoption"
                  subtitle="Live count of users who have not yet enrolled in any MFA method."
               />
               <CardText>
                  <div className="md-grid mfa-policy-kpi-grid">
                     <div className="md-cell md-cell--3 mfa-policy-kpi">
                        <span className="mfa-policy-kpi-value mfa-policy-kpi-value--red">
                           {isLoading ? '—' : formatStat(unenrolled)}
                        </span>
                        <span className="mfa-policy-kpi-label">Unenrolled</span>
                        <span className="mfa-policy-kpi-sub">
                           Users skipped MFA setup
                        </span>
                     </div>
                     <div className="md-cell md-cell--3 mfa-policy-kpi">
                        <span className="mfa-policy-kpi-value mfa-policy-kpi-value--green">
                           {isLoading ? '—' : formatStat(authenticatorEnrolled)}
                        </span>
                        <span className="mfa-policy-kpi-label">
                           Authenticator
                        </span>
                        <span className="mfa-policy-kpi-sub">
                           Enrolled via authenticator app
                        </span>
                     </div>
                     <div className="md-cell md-cell--3 mfa-policy-kpi">
                        <span className="mfa-policy-kpi-value mfa-policy-kpi-value--green">
                           {isLoading ? '—' : formatStat(smsEnrolled)}
                        </span>
                        <span className="mfa-policy-kpi-label">SMS</span>
                        <span className="mfa-policy-kpi-sub">
                           Enrolled via SMS one-time code
                        </span>
                     </div>
                     <div className="md-cell md-cell--3 mfa-policy-kpi">
                        <span className="mfa-policy-kpi-value">
                           {isLoading
                              ? '—'
                              : formatStat(
                                   mfaStatistics?.totalActiveUsers ?? null
                                )}
                        </span>
                        <span className="mfa-policy-kpi-label">
                           Active Users
                        </span>
                        <span className="mfa-policy-kpi-sub">
                           Total active accounts since MFA rollout
                        </span>
                     </div>
                  </div>
               </CardText>
            </Card>
         </div>
      </div>
   );
};

export default MfaPolicy;
